<template>
  <nuxt-link :to="getProductURL()" @click="pushGtmSource">
    <v-card class="product-card" elevation="0">
      <div class="cover-block">
        <v-img :src="coverImage" cover class="wd-bg-gray30" :aspect-ratio="16 / 9" gradient="to top, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.3)" />

        <div class="tags-bar">
          <v-chip v-for="(tagChip, idx) in tagChips" :key="idx" class="tag-chip" :class="tagChip.className" label>
            <FunnowSvgIcon v-if="tagChip.iconFilename" class="wd-mr-1 wd-text-gray10" max-width="16" :image-path="tagChip.iconFilename" />
            {{ tagChip.text }}
          </v-chip>
        </div>
      </div>
      <div class="px-2 pt-3 product-info">
        <div class="card-title mb-2 d-flex justify-space-between align-center product-line">
          <component :is="highlightProductName ? 'span' : 'h3'" class="branch-name mb-0 text-truncate" :data-testid="testIDs.productCard.branchName">
            {{ product.branch_name }}
          </component>
          <p v-if="!isBookSeatOnly && product.onboard" class="price mb-0 wd-text-sm" v-html="productDiscount"></p>
        </div>

        <div class="mb-2 d-flex product-price-info">
          <component
            :is="highlightProductName ? 'h3' : 'span'"
            class="product-name mr-2"
            :class="{ 'mb-0': highlightProductName, 'font-weight-regular': highlightProductName }"
          >
            {{ productName }}
          </component>
          <v-spacer />
          <span v-if="shouldShowOriginalPrice" class="srrp">
            <del> {{ changeThousandsSeparators(product.rrp) }} </del>
          </span>
          <span v-if="isPaidOnsite" class="wd-text-black50 wd-text-sm wd-min-w-max">
            {{ $t('pay_at_store') }}
          </span>
        </div>

        <div class="wd-inline-flex wd-flex-wrap wd-w-full wd-items-center" :class="[hideRating ? 'wd-justify-end' : 'wd-justify-between']">
          <div v-if="!hideRating" class="wd-text-sm wd-flex wd-items-center">
            <funnow-icon class="mr-1 wd-w-5 !wd-min-w-0" max-width="20" :image-path="'star_orange.svg'" />
            <span class="mr-1"> {{ product.rating }} </span>
            <span :data-testid="testIDs.productCard.reviewCount"> ({{ product.number_of_comments }}) </span>
          </div>

          <v-chip v-if="sellTypeChip" label class="tag" :class="[`tag--${sellTypeChip.classType}`]">
            <funnow-icon
              v-if="product.sell_time_type === 'now' && !isDisabledByElite"
              class="mr-1 wd-w-4 !wd-min-w-0"
              max-width="16"
              :image-path="'flash-icon.svg'"
            />
            <span :style="{ overflow: 'hidden' }"> {{ sellTypeChip.text }} </span>
          </v-chip>
        </div>
      </div>
    </v-card>
  </nuxt-link>
</template>

<script>
import { mapGetters } from 'vuex'

import utils from '@/utils'
import testIDs from '@/constants/test-ids.json'
import { isPriceReduced } from '@/utils/common'
import { useProductPlatformInfo } from '@/composition/useProductInfo'
import { useElite } from '@/composition/useMember'

import FunnowSvgIcon from '@/components/icons/funnow-svg-icon.vue'
import FunnowIcon from '@/components/icons/funnow-icon'
import { usePlatformStore } from '@/store-pinia/platform'
import { useGtmSourceInject, pushGtmSource as pushGtmSourceToDataLayer } from '@/composition/useGtmSource'

export default defineComponent({
  name: 'ProductCard',
  components: {
    FunnowSvgIcon,
    FunnowIcon,
  },
  props: {
    product: { type: Object, default: () => ({}) },
    bringQuery: { type: Boolean, default: true },
    hideRrp: { type: Boolean, default: false },
    hideRating: { type: Boolean, default: false },
    highlightProductName: { type: Boolean, default: false },
  },
  setup(props) {
    const platformStore = usePlatformStore()
    const { isFunBookV2 } = storeToRefs(platformStore)
    const branchId = platformStore.getBranchId()
    const productName = computed(() => (isFunBookV2.value ? props.product.fb_product_name : props.product.product_name) || props.product.product_name)
    const isOnlyShowFlashSaleTag = computed(() => isFunBookV2.value)

    const productFromProps = computed(() => props.product)
    const { isBookSeatOnly, isPaidOnsite } = useProductPlatformInfo(productFromProps)
    const { isDisabledByElite } = useElite(props.product)
    const localePath = useLocalePath()

    const gtmSource = useGtmSourceInject()

    function handlePushGtmSource() {
      if (gtmSource) {
        pushGtmSourceToDataLayer(gtmSource)
      }
    }

    return {
      productName,
      isOnlyShowFlashSaleTag,
      isFunBookV2,
      branchId,
      isBookSeatOnly,
      isPaidOnsite,
      isDisabledByElite,
      testIDs,
      pushGtmSource: handlePushGtmSource,
      localePath,
    }
  },
  data() {
    return {
      imageLoading: false,
      images: [],
    }
  },
  computed: {
    shouldShowOriginalPrice() {
      // 顯示原價的條件：hideRrp 為 false、價格有降價、商品已上架且非僅訂位
      return !this.hideRrp && isPriceReduced(this.product.discount, this.product.rrp) && this.product.onboard && !this.isBookSeatOnly
    },
    tagChips() {
      const tagChips = []
      const tagType = {
        limited: { className: ['limited-tag'], text: this.$t('tag_limited') },
        flashSale: { className: ['flash-sale-tag'], text: this.$t('tag_flash_sale_now'), iconFilename: 'promo_code.svg' },
        hotSale: { className: ['hot-tag'], text: this.$t('tag_hot') },
        newSale: { className: ['new-tag'], text: this.$t('tag_new') },
        vip: { className: ['vip-tag'], text: this.$t('tag_special_gift'), iconFilename: 'gift.svg' },
        elite: { className: ['elite-tag'], text: this.$t('tag_elite') },
        birthday: { className: ['birthday-tag'], text: this.$t('tag_birthday_gift'), iconFilename: 'gift.svg' },
      }

      if (this.isOnlyShowFlashSaleTag) {
        if (this.product.flash_time_type && this.product.flash_time_type !== 'hidden') {
          switch (this.product.flash_time_type) {
            case 'day': {
              const date = this.$dayjs(this.product.flash_time).format('MM/DD')
              tagType.flashSale.text = this.$t('tag_flash_sale_time', [date])
              break
            }
            case 'hour': {
              const time = this.$dayjs(this.product.flash_time).format('HH:mm')
              tagType.flashSale.text = this.$t('tag_flash_sale_time', [time])
              break
            }
          }
          tagChips.push(tagType.flashSale)
        }
        return tagChips
      }

      // 限量釋出
      if (this.product.is_limited) {
        tagChips.push(tagType.limited)
        return tagChips
      }

      // 限時促銷
      if (this.product.flash_time_type && this.product.flash_time_type !== 'hidden') {
        switch (this.product.flash_time_type) {
          case 'day': {
            const date = this.$dayjs(this.product.flash_time).format('MM/DD')
            tagType.flashSale.text = this.$t('tag_flash_sale_time', [date])
            break
          }
          case 'hour': {
            const time = this.$dayjs(this.product.flash_time).format('HH:mm')
            tagType.flashSale.text = this.$t('tag_flash_sale_time', [time])
            break
          }
        }
        tagChips.push(tagType.flashSale)
        return tagChips
      }

      // Lv.1 順序：加碼禮 → 生日禮 → 菁英會員限定 → 熱門 → 新上架，最多顯示 3 個
      if (this.product.vip_reward) {
        tagChips.push(tagType.vip)
      }
      if (this.product.birthday_reward) {
        tagChips.push(tagType.birthday)
      }
      if (this.product.is_elite) {
        tagChips.push(tagType.elite)
      }
      if (this.product.is_hot) {
        tagChips.push(tagType.hotSale)
      }
      if (this.product.is_new_product) {
        tagChips.push(tagType.newSale)
      }
      return tagChips.slice(0, 3)
    },
    sellTypeChip() {
      if (this.product.onboard === 0 || this.isDisabledByElite) {
        const text = this.product.onboard === 0 ? this.$t('all_sold_out') : this.$t('elite_member_only')
        return { text, classType: 'closed' }
      } else {
        switch (this.product.sell_time_type) {
          case 'now':
            return { text: this.$t('package_available_now'), classType: 'now' }
          case 'hidden':
            return false
          case 'day':
          case 'hour': {
            const dateForamt = this.product.sell_time_type === 'day' ? 'MM/DD' : 'HH:mm'
            const sellDateTime = this.$dayjs(this.product.sell_time).format(dateForamt)
            const dayText = this.$t('package_start_booking_time', [sellDateTime])
            return { text: dayText, classType: 'day' }
          }
          default:
            return false
        }
      }
    },
    productDiscount() {
      const price = this.changeThousandsSeparators(this.product.discount)
      const textHtml = `${this.currency} <span class="wd-text-orange50" data-testid="${this.testIDs.productCard.price}"> ${price} </span>`
      const discountText = this.$t('price_up', [textHtml])
      return discountText
    },
    coverImage() {
      return this.product.cover_image ? `${utils.CDN}${utils.imagePath(this.product.cover_image)}` : ''
    },
    currency() {
      return (
        this.allCurrency.filter(obj => {
          return obj.id === this.product.currency_id
        })?.[0]?.code || ''
      )
    },
    ...mapGetters({
      allCurrency: 'currency/allCurrency',
    }),
  },
  methods: {
    changeThousandsSeparators(number) {
      const price = parseFloat(number)
      if (price) {
        return price.toLocaleString()
      }
      return number
    },
    getThumbImage(path) {
      return `${utils.CDN}${utils.thumbImagePath(path)}`
    },
    getProductURL() {
      const routerQuery = this.bringQuery ? this.$route.query : {}
      const routeName = this.isFunBookV2 ? 'funbook-branch_id-products-product_id' : 'products-id'
      const params = this.isFunBookV2 ? { branch_id: this.branchId, product_id: this.product.product_id } : { id: this.product.product_id }
      return this.localePath({ name: routeName, params: params, query: routerQuery })
    },
    addFavorite(bid) {
      console.log('add branch_id = ' + bid + ' to favorites')
    },
  },
})
</script>

<style scoped lang="scss" src="@/assets/sass/components/card/product.scss"></style>
